/**
 * PetalsList Component
 *
 * Displays a list of petals grouped by flower type.
 * Supports selection for editing and deletion.
 */

import clsx from 'clsx';
import {
  PetalDto,
  FlowerKey,
  FLOWER_KEYS,
  FLOWER_LABELS,
  FLOWER_DESCRIPTIONS,
  CORE_PETAL_LABELS,
} from '../../store/api/storyPrepApi';

interface PetalsListProps {
  petals: PetalDto[];
  selectedPetalId?: string | null;
  onSelect: (petal: PetalDto) => void;
  onDelete: (petalId: string) => void;
  isLoading?: boolean;
}

// Flower color mapping for visual distinction
const FLOWER_COLORS: Record<FlowerKey, { bg: string; border: string; text: string }> = {
  problem: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
  pain: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
  power: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
  product: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  plan: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700' },
  path: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700' },
  place: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  pushback: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700' },
  promise: { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700' },
};

export function PetalsList({
  petals,
  selectedPetalId,
  onSelect,
  onDelete,
  isLoading,
}: PetalsListProps) {
  // Group petals by flower
  const petalsByFlower = FLOWER_KEYS.reduce(
    (acc, flower) => {
      acc[flower] = petals.filter((p) => p.flower === flower);
      return acc;
    },
    {} as Record<FlowerKey, PetalDto[]>
  );

  // Count total petals
  const totalPetals = petals.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">
          Your Petals ({totalPetals})
        </h3>
      </div>

      {/* Empty state */}
      {totalPetals === 0 && (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">🌸</div>
          <p className="text-sm">No petals yet. Create your first petal!</p>
        </div>
      )}

      {/* Grouped petals */}
      <div className="space-y-3">
        {FLOWER_KEYS.map((flower) => {
          const flowerPetals = petalsByFlower[flower];
          if (flowerPetals.length === 0) return null;

          const colors = FLOWER_COLORS[flower];

          return (
            <div key={flower} className="space-y-2">
              {/* Flower header */}
              <div
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 rounded-md',
                  colors.bg
                )}
              >
                <span className={clsx('text-xs font-semibold uppercase', colors.text)}>
                  {FLOWER_LABELS[flower]}
                </span>
                <span className="text-xs text-gray-500">
                  ({flowerPetals.length})
                </span>
              </div>

              {/* Petals in this flower */}
              <div className="space-y-1 pl-2">
                {flowerPetals.map((petal) => (
                  <PetalItem
                    key={petal.id}
                    petal={petal}
                    isSelected={selectedPetalId === petal.id}
                    colors={colors}
                    onSelect={() => onSelect(petal)}
                    onDelete={() => onDelete(petal.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Flower legend (collapsed by default) */}
      {totalPetals === 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-2">Flower Types:</p>
          <div className="grid grid-cols-3 gap-1">
            {FLOWER_KEYS.map((flower) => (
              <div
                key={flower}
                className="text-xs text-gray-500"
                title={FLOWER_DESCRIPTIONS[flower]}
              >
                <span className={FLOWER_COLORS[flower].text}>●</span>{' '}
                {FLOWER_LABELS[flower]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Individual petal item
function PetalItem({
  petal,
  isSelected,
  colors,
  onSelect,
  onDelete,
}: {
  petal: PetalDto;
  isSelected: boolean;
  colors: { bg: string; border: string; text: string };
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className={clsx(
        'group flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all',
        isSelected
          ? `${colors.bg} ${colors.border} ring-2 ring-primary-300`
          : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      )}
      onClick={onSelect}
    >
      {/* Petal info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">
            {petal.title}
          </span>
          <span
            className={clsx(
              'text-xs px-1.5 py-0.5 rounded',
              colors.bg,
              colors.text
            )}
          >
            {CORE_PETAL_LABELS[petal.coreType]}
          </span>
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5">
          {petal.content.substring(0, 60)}
          {petal.content.length > 60 ? '...' : ''}
        </p>
      </div>

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (confirm('Delete this petal?')) {
            onDelete();
          }
        }}
        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
        title="Delete petal"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    </div>
  );
}

export default PetalsList;
